// src/pages/players/PlayerIdCard.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import playerService from '../../services/playerservice';  // Ajuste para 'playerservice' se for o nome do seu arquivo
import './PlayerIdCard.css'; 

function PlayerIdCard() {
  const { playerId } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true); // Loading para busca inicial do jogador
  const [isVerifying, setIsVerifying] = useState(false); // Loading para verificação do CPF
  
  const [playerImgSrc, setPlayerImgSrc] = useState('/default-player-photo.png');
  
  // Novos estados para o fluxo de verificação de CPF
  const [showCpfInput, setShowCpfInput] = useState(true); // Começa mostrando o input de CPF
  const [enteredCpf, setEnteredCpf] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [canShowCard, setCanShowCard] = useState(false); // Controla se a carteirinha pode ser exibida

  // Guarda os dados do jogador carregado para comparar o CPF depois
  const [loadedPlayerData, setLoadedPlayerData] = useState(null);


  // Busca inicial apenas dos dados básicos do jogador (sem precisar do time agora)
  useEffect(() => {
    const fetchMinimalPlayerData = async () => {
      if (!playerId) {
        setError("ID do jogador não fornecido.");
        setIsLoading(false);
        setShowCpfInput(false); // Não mostrar input de CPF se não há ID
        return;
      }
      setIsLoading(true);
      setError('');
      try {
        const playerResponse = await playerService.getPlayerById(playerId); // Ajuste se seu serviço já retorna .data
        const playerData = playerResponse?.data;

        if (playerData && typeof playerData === 'object' && Object.keys(playerData).length > 0) {
          setLoadedPlayerData(playerData); // Guarda todos os dados para usar após verificação
          // Não define 'player' ainda, só após verificação do CPF
        } else {
          setError('Jogador não encontrado.');
          setShowCpfInput(false);
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Falha ao carregar dados básicos do jogador.');
        setShowCpfInput(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMinimalPlayerData();
  }, [playerId]);


  const handleCpfInputChange = (e) => {
    setEnteredCpf(e.target.value);
    if(cpfError) setCpfError(''); // Limpa o erro ao digitar
  };

  const handleCpfVerification = async (e) => {
    e.preventDefault();
    if (!enteredCpf.trim()) {
      setCpfError('Por favor, insira o CPF.');
      return;
    }
    if (!loadedPlayerData) {
      setCpfError('Dados do jogador não carregados. Tente novamente.');
      return;
    }

    setIsVerifying(true);
    setCpfError('');

    // Simulação de verificação - idealmente, isso poderia ser feito no backend
    // por segurança, mas para o frontend, comparamos o CPF digitado
    // com o CPF armazenado em loadedPlayerData.
    // Certifique-se que o CPF está armazenado sem formatação no banco (só números)
    // ou normalize ambos antes de comparar.
    const normalizeCpf = (cpf) => String(cpf).replace(/\D/g, ''); // Remove não dígitos

    const storedCpf = normalizeCpf(loadedPlayerData.cpf);
    const inputCpf = normalizeCpf(enteredCpf);

    // Apenas um pequeno delay para simular verificação
    await new Promise(resolve => setTimeout(resolve, 500)); 

    if (storedCpf === inputCpf) {
      console.log("CPF verificado com sucesso!");
      setPlayer(loadedPlayerData); // Agora sim, define o jogador para exibição
      if (loadedPlayerData.photo) {
        if (typeof playerService.getPlayerPhotoUrl === 'function') {
            setPlayerImgSrc(playerService.getPlayerPhotoUrl(loadedPlayerData.photo));
        } else {
            const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api$/, '');
            setPlayerImgSrc(`${baseUrl}/uploads/${loadedPlayerData.photo}`);
        }
      } else {
        setPlayerImgSrc('/default-player-photo.png');
      }
      setShowCpfInput(false); // Esconde input de CPF
      setCanShowCard(true);  // Permite exibir a carteirinha
    } else {
      console.log("Falha na verificação de CPF. Esperado:", storedCpf, "Recebido:", inputCpf);
      setCpfError('CPF incorreto ou não corresponde ao do jogador.');
      setPlayer(null); // Garante que player não seja setado
      setCanShowCard(false);
    }
    setIsVerifying(false);
  };


  const handleImageError = (e) => {
    setPlayerImgSrc('/default-player-photo.png');
    e.target.onerror = null;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '___/___/_____';
    try {
      const dateParts = String(dateString).split('T')[0].split('-');
      if (dateParts.length === 3) {
        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);
        return new Date(year, month, day).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch(e) { return 'Inválida'; }
  };


  if (isLoading) return <div className="id-card-loading">Carregando informações...</div>;
  if (error) return <div className="id-card-error"><h2>Erro</h2><p>{error}</p><button onClick={() => navigate(-1)}>Voltar</button></div>;
  
  // Se ainda não pode mostrar a carteirinha E o input de CPF deve ser mostrado
  if (showCpfInput && !canShowCard) {
    return (
      <div className="player-id-card-page-container cpf-verification-container">
        <h2 className="id-card-title" style={{marginBottom: '1.5rem', fontSize:'1.2rem'}}>Verificação de Identidade</h2>
        <p style={{marginBottom: '1rem', textAlign: 'center', fontSize: '0.9rem'}}>Para acessar a carteirinha do jogador, por favor, insira o CPF.</p>
        <form onSubmit={handleCpfVerification} className="cpf-form">
          <div className="form-group">
            <label htmlFor="cpfInput" className="form-label">CPF do Jogador:</label>
            <input
              type="text"
              id="cpfInput"
              value={enteredCpf}
              onChange={handleCpfInputChange}
              placeholder="Digite o CPF"
              className="form-input" // Reutilize estilos de form do PlayerForm.css ou crie novos
              required
              disabled={isVerifying}
            />
          </div>
          {cpfError && <p className="error-message cpf-error">{cpfError}</p>}
          <button type="submit" className="button button-primary verify-button" disabled={isVerifying}>
            {isVerifying ? 'Verificando...' : 'Verificar e Mostrar Carteirinha'}
          </button>
        </form>
         <button onClick={() => navigate(-1)} className="button button-secondary" style={{marginTop: '1rem'}}>
          Cancelar e Voltar
        </button>
      </div>
    );
  }


  if (!player || !canShowCard) {
    // Se chegou aqui após a tentativa de verificação e falhou, ou se o jogador não foi carregado.
    // A mensagem de erro de CPF ou de carregamento de jogador já teria sido mostrada.
    // Este é um fallback caso algo mais dê errado.
    if (!isLoading && !error && !showCpfInput && !canShowCard) { // Só mostra se não for loading, não tiver erro geral e já passou da tela de CPF
        return <div className="id-card-error"><p>Não foi possível exibir a carteirinha. Verifique o CPF ou tente novamente.</p><button onClick={() => navigate(-1)}>Voltar</button></div>;
    }
    return null; // Não renderiza nada se estiver em um estado intermediário
  }


  // Se chegou aqui, CPF foi verificado e player está definido.
  return (
    <div className="player-id-card-page-container">
      <div className="player-id-card simple-card"> {/* Classe adicional para simplificar layout */}
        {/* CABEÇALHO REMOVIDO */}
        {/* <div className="id-card-header"> ... </div> */}

        <div className="id-card-content">
          <div className="id-card-player-photo-container">
            <img 
                src={playerImgSrc} 
                alt={`Foto de ${player.name}`} 
                className="id-card-player-photo"
                onError={handleImageError}
            />
          </div>
          <div className="id-card-player-info">
            <div className="info-field">
              <span className="label">NOME:</span>
              <span className="value name-value">{player.name?.toUpperCase() || 'NÃO DISPONÍVEL'}</span>
            </div>
            <div className="info-field">
              <span className="label">NASCIMENTO:</span>
              <span className="value">{formatDate(player.birthDate)}</span>
            </div>
            <div className="info-field">
              <span className="label">CPF:</span>
              <span className="value">{player.cpf || 'NÃO INFORMADO'}</span> {/* Exibe o CPF aqui */}
            </div>
            <div className="info-field">
              <span className="label">POSIÇÃO:</span>
              <span className="value">{player.position?.toUpperCase() || 'NÃO INFORMADA'}</span>
            </div>
            {/* CAMPO EQUIPE REMOVIDO */}
            {/* <div className="info-field">
              <span className="label">EQUIPE:</span>
              <span className="value team-name-value">{team?.name?.toUpperCase() || 'NÃO VINCULADO'}</span>
            </div> */}
          </div>
        </div>
        <div className="id-card-footer simplified-footer"> {/* Classe para simplificar rodapé */}
          <p>Documento de Atleta</p>
        </div>
      </div>

      <div className="id-card-actions">
        <button onClick={() => window.print()} className="button button-primary">
          Imprimir Carteirinha
        </button>
        <button onClick={() => navigate(`/players/${playerId}`)} className="button button-secondary">
          Voltar para Detalhes
        </button>
      </div>
    </div>
  );
}

export default PlayerIdCard;